import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import {
  CheckSquare,
  Plus,
  Trash2,
  CheckCircle2,
  Circle,
  ChevronLeft,
  ChevronRight,
  History,
  CalendarDays,
  Check,
  Edit2,
  X,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';
import { Task } from '../../types';
import { getLocalDateString, shiftDays, formatDisplayDate, parseLocalDate } from '../../lib/dateUtils';

export const TasksView: React.FC = () => {
  const { tasks, addTask, toggleTask, editTask, deleteTask, t } = useApp();

  const todayStr = getLocalDateString();
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [quickTaskText, setQuickTaskText] = useState('');
  const [viewMode, setViewMode] = useState<'day' | 'history'>('day');
  const [isCompletedExpanded, setIsCompletedExpanded] = useState(true);

  // Inline editing state
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingTaskTitle, setEditingTaskTitle] = useState('');

  // Date Navigation Helpers
  const shiftDate = (days: number) => {
    setSelectedDate((prev) => shiftDays(prev, days));
  };

  const isSelectedToday = selectedDate === todayStr;

  const formattedSelectedDate = useMemo(() => {
    return formatDisplayDate(selectedDate);
  }, [selectedDate]);

  // Tasks for currently selected day - separated into Pending (Top) and Completed (Bottom)
  const dayTasks = useMemo(() => {
    return tasks.filter((t) => {
      const taskDate = t.dueDate || t.createdAt;
      return taskDate === selectedDate;
    });
  }, [tasks, selectedDate]);

  const dayPendingTasks = useMemo(
    () => dayTasks.filter((t) => t.status === 'pending'),
    [dayTasks]
  );
  const dayCompletedTasks = useMemo(
    () => dayTasks.filter((t) => t.status === 'completed'),
    [dayTasks]
  );

  const dayTotal = dayTasks.length;
  const dayCompletedCount = dayCompletedTasks.length;
  const dayProgressPercent = dayTotal > 0 ? Math.round((dayCompletedCount / dayTotal) * 100) : 0;

  // Group all tasks by date for the Day-by-Day History Archive
  const dailyHistory = useMemo(() => {
    const dateMap: Record<string, Task[]> = {};

    tasks.forEach((t) => {
      const dateKey = t.dueDate || t.createdAt || todayStr;
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = [];
      }
      dateMap[dateKey].push(t);
    });

    // Sort dates in descending order (most recent first)
    const sortedDates = Object.keys(dateMap).sort((a, b) => b.localeCompare(a));

    return sortedDates.map((dateKey) => {
      const dayList = dateMap[dateKey];
      const completed = dayList.filter((t) => t.status === 'completed').length;
      const total = dayList.length;
      const label = formatDisplayDate(dateKey, {
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });

      return {
        date: dateKey,
        label,
        tasks: dayList,
        completed,
        total,
        percent: total > 0 ? Math.round((completed / total) * 100) : 0,
      };
    });
  }, [tasks, todayStr]);

  // Handle adding quick simple task
  const handleAddQuickTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickTaskText.trim()) return;

    addTask({
      title: quickTaskText.trim(),
      priority: 'medium',
      dueDate: selectedDate,
      recurring: 'none',
    });

    setQuickTaskText('');
  };

  // Handle saving inline task edit
  const handleSaveTaskEdit = (id: string) => {
    if (!editingTaskTitle.trim()) return;
    editTask(id, { title: editingTaskTitle.trim() });
    setEditingTaskId(null);
    setEditingTaskTitle('');
  };

  const renderTaskItem = (task: Task) => {
    const isCompleted = task.status === 'completed';
    const isEditing = editingTaskId === task.id;

    const handleBoxClick = () => {
      if (!isEditing) {
        toggleTask(task.id);
      }
    };

    return (
      <div
        key={task.id}
        onClick={handleBoxClick}
        className={`p-4 bg-white dark:bg-[#111827] border rounded-3xl transition-all duration-200 flex items-center justify-between gap-3 cursor-pointer select-none ${
          isCompleted
            ? 'border-slate-200/60 dark:border-slate-800/60 opacity-70 bg-slate-50/60 dark:bg-slate-900/40'
            : 'border-slate-200/80 dark:border-slate-800 hover:border-blue-500/50 hover:bg-blue-50/20 dark:hover:bg-blue-950/20 shadow-xs'
        }`}
      >
        <div className="flex items-center gap-3.5 min-w-0 flex-1">
          {/* Checkbox */}
          <div className="text-slate-400 hover:text-blue-500 transition-colors flex-shrink-0">
            {isCompleted ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-500 fill-emerald-500/20" />
            ) : (
              <Circle className="w-5 h-5 hover:stroke-blue-500" />
            )}
          </div>

          {/* Task Text or Inline Edit Form */}
          {isEditing ? (
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2 flex-1"
            >
              <input
                type="text"
                value={editingTaskTitle}
                onChange={(e) => setEditingTaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTaskEdit(task.id);
                  if (e.key === 'Escape') setEditingTaskId(null);
                }}
                className="flex-1 px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-blue-500 rounded-xl text-sm font-semibold text-slate-900 dark:text-white focus:outline-none"
                autoFocus
              />
              <button
                onClick={() => handleSaveTaskEdit(task.id)}
                className="p-1.5 bg-blue-500 text-white rounded-xl text-xs font-bold"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setEditingTaskId(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <span
              className={`text-sm font-bold truncate transition-colors ${
                isCompleted
                  ? 'line-through text-slate-400 dark:text-slate-500'
                  : 'text-slate-900 dark:text-white'
              }`}
            >
              {task.title}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingTaskId(task.id);
              setEditingTaskTitle(task.title);
            }}
            className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title="Edit Task"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteTask(task.id);
            }}
            className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-colors"
            title="Delete Task"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in pb-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <CheckSquare className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                {t.tasks}
              </h1>
            </div>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200/80 dark:border-slate-700 w-full sm:w-auto">
          <button
            onClick={() => setViewMode('day')}
            className={`flex-1 sm:flex-initial justify-center px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
              viewMode === 'day'
                ? 'bg-white dark:bg-[#111827] text-blue-600 dark:text-blue-400 shadow-xs'
                : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <CalendarDays className="w-3.5 h-3.5" />
            <span>Daily Tasks</span>
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={`flex-1 sm:flex-initial justify-center px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
              viewMode === 'history'
                ? 'bg-white dark:bg-[#111827] text-purple-600 dark:text-purple-400 shadow-xs'
                : 'text-slate-500 hover:text-purple-600 dark:hover:text-purple-400'
            }`}
          >
            <History className="w-3.5 h-3.5" />
            <span>Day-by-Day Record</span>
          </button>
        </div>
      </div>

      {/* VIEW MODE: DAY VIEW */}
      {viewMode === 'day' && (
        <div className="space-y-5">
          {/* Date Navigator Bar */}
          <div className="p-3 sm:p-4 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 w-full max-w-full min-w-0">
            <div className="flex items-center justify-between sm:justify-start gap-1.5 sm:gap-2 w-full sm:w-auto">
              <button
                onClick={() => shiftDate(-1)}
                className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors flex-shrink-0"
                title="Previous Day"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="flex items-center justify-center gap-1.5 min-w-0 flex-1 sm:flex-initial px-1">
                <span className="text-xs sm:text-sm font-black text-slate-900 dark:text-white truncate">
                  {formattedSelectedDate}
                </span>

                {isSelectedToday && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20 flex-shrink-0">
                    Today
                  </span>
                )}
              </div>

              <button
                onClick={() => shiftDate(1)}
                className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors flex-shrink-0"
                title="Next Day"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Quick Date Switcher & Date Picker */}
            <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto">
              {!isSelectedToday ? (
                <button
                  onClick={() => setSelectedDate(todayStr)}
                  className="px-3 py-1.5 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded-xl text-xs font-bold border border-blue-200 dark:border-blue-800/40 hover:bg-blue-100 flex-shrink-0"
                >
                  Jump to Today
                </button>
              ) : (
                <div className="text-[11px] text-slate-400 font-medium hidden sm:block">Today Selected</div>
              )}

              <div className="relative flex-1 sm:flex-initial flex justify-end">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full sm:w-auto px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer text-center sm:text-left"
                />
              </div>
            </div>
          </div>

          {/* Simple Word Task Input Bar (Instant 1-Click Task Add) */}
          <form
            onSubmit={handleAddQuickTask}
            className="p-2 sm:p-2.5 bg-white dark:bg-[#111827] border-2 border-blue-500/30 hover:border-blue-500 rounded-3xl shadow-sm flex items-center gap-1.5 sm:gap-2 transition-all w-full max-w-full min-w-0"
          >
            <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center flex-shrink-0 ml-1 sm:ml-2">
              <Plus className="w-4 h-4" />
            </div>

            <input
              type="text"
              placeholder={isSelectedToday ? 'Write a task for today...' : 'Write a task...'}
              value={quickTaskText}
              onChange={(e) => setQuickTaskText(e.target.value)}
              className="flex-1 min-w-0 px-2 py-2 bg-transparent text-sm font-semibold text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
              autoFocus
            />

            <button
              type="submit"
              disabled={!quickTaskText.trim()}
              className={`px-3 sm:px-4 py-2 rounded-2xl text-xs font-bold transition-all flex items-center gap-1.5 flex-shrink-0 ${
                quickTaskText.trim()
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-500/25 active:scale-95'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
              }`}
            >
              <Check className="w-3.5 h-3.5" />
              <span>Add Task</span>
            </button>
          </form>

          {/* Daily Progress Overview */}
          {dayTotal > 0 && (
            <div className="p-4 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-xs space-y-2">
              <div className="flex items-center justify-between text-xs font-bold">
                <span className="text-slate-600 dark:text-slate-300">
                  {dayCompletedCount} of {dayTotal} tasks completed
                </span>
                <span className="text-blue-600 dark:text-blue-400 font-mono">
                  {dayProgressPercent}%
                </span>
              </div>
              <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${dayProgressPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Tasks List for the Selected Day */}
          {dayTotal === 0 ? (
            <div className="p-12 text-center bg-white dark:bg-[#111827] rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 text-blue-500 flex items-center justify-center mx-auto">
                <CheckSquare className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                No tasks recorded for this day
              </h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                Type your task in the box above and press Enter to start recording your day&apos;s to-dos.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* PENDING TASKS (Top Section) */}
              {dayPendingTasks.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 px-1">
                    <Circle className="w-3.5 h-3.5 text-blue-500" />
                    <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      To Do ({dayPendingTasks.length})
                    </h2>
                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                  </div>

                  <div className="space-y-2.5">
                    {dayPendingTasks.map((task) => renderTaskItem(task))}
                  </div>
                </div>
              )}

              {/* COMPLETED TASKS (Bottom Section - Auto-moved when completed) */}
              {dayCompletedTasks.length > 0 && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Completed ({dayCompletedTasks.length})
                      </h2>
                    </div>

                    <button
                      onClick={() => setIsCompletedExpanded(!isCompletedExpanded)}
                      className="text-xs font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center gap-1"
                    >
                      <span>{isCompletedExpanded ? 'Hide' : 'Show'}</span>
                      {isCompletedExpanded ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>

                  <div className="h-px bg-slate-200 dark:bg-slate-800" />

                  {isCompletedExpanded && (
                    <div className="space-y-2.5">
                      {dayCompletedTasks.map((task) => renderTaskItem(task))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* VIEW MODE: DAY-BY-DAY HISTORY ARCHIVE */}
      {viewMode === 'history' && (
        <div className="space-y-4">
          <div className="p-4 bg-purple-500/10 border border-purple-500/20 text-purple-900 dark:text-purple-300 rounded-2xl text-xs flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <span>
                Browse all past and scheduled days to see what tasks were planned and completed on each date.
              </span>
            </div>
          </div>

          {dailyHistory.length === 0 ? (
            <div className="p-12 text-center bg-white dark:bg-[#111827] rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 text-purple-500 flex items-center justify-center mx-auto">
                <History className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                No task history recorded yet
              </h3>
              <p className="text-xs text-slate-500">
                Tasks created on any day will automatically form a daily record archive here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dailyHistory.map((dayItem) => {
                const isDayToday = dayItem.date === todayStr;

                return (
                  <div
                    key={dayItem.date}
                    onClick={() => {
                      setSelectedDate(dayItem.date);
                      setViewMode('day');
                    }}
                    className={`p-5 bg-white dark:bg-[#111827] border rounded-3xl transition-all cursor-pointer shadow-xs hover:shadow-lg hover:-translate-y-0.5 space-y-3 ${
                      isDayToday
                        ? 'border-blue-500 ring-2 ring-blue-500/20'
                        : 'border-slate-200/80 dark:border-slate-800 hover:border-purple-500/40'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <h3 className="text-sm font-black text-slate-900 dark:text-white">
                          {dayItem.label}
                        </h3>
                        <span className="text-[10px] font-mono text-slate-400">{dayItem.date}</span>
                      </div>

                      {isDayToday && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                          Today
                        </span>
                      )}
                    </div>

                    {/* Completion status */}
                    <div className="flex items-center justify-between text-xs font-bold pt-1">
                      <span className="text-slate-500">
                        {dayItem.completed} / {dayItem.total} completed
                      </span>
                      <span className="font-mono text-blue-600 dark:text-blue-400">
                        {dayItem.percent}%
                      </span>
                    </div>

                    <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full"
                        style={{ width: `${dayItem.percent}%` }}
                      />
                    </div>

                    {/* Preview of first 3 tasks */}
                    <div className="space-y-1 pt-1">
                      {dayItem.tasks.slice(0, 3).map((t) => (
                        <div
                          key={t.id}
                          className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400"
                        >
                          {t.status === 'completed' ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          ) : (
                            <Circle className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                          )}
                          <span
                            className={`truncate ${
                              t.status === 'completed' ? 'line-through text-slate-400' : ''
                            }`}
                          >
                            {t.title}
                          </span>
                        </div>
                      ))}

                      {dayItem.tasks.length > 3 && (
                        <span className="text-[10px] text-slate-400 font-semibold pl-5 block">
                          +{dayItem.tasks.length - 3} more tasks...
                        </span>
                      )}
                    </div>

                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[11px] text-blue-600 dark:text-blue-400 font-bold">
                      <span>View & Manage Day →</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
